/**
 * Error definitions and error codes for Bifrost RPC Framework
 */

import { RpcError } from "./protocol";

/**
 * Error code ranges
 */
export enum ErrorCategory {
  TRANSPORT = 1000, // 1000-1999: Communication errors
  PROTOCOL = 2000, // 2000-2999: Protocol violations
  SERVICE = 3000, // 3000-3999: Service errors
  APPLICATION = 4000, // 4000-4999: Business logic errors
  INTERNAL = 5000, // 5000-5999: System errors
}

/**
 * Standard error codes
 */
export enum ErrorCode {
  // Transport errors (1000-1999)
  CONNECTION_FAILED = 1001,
  CONNECTION_TIMEOUT = 1002,
  MESSAGE_TOO_LARGE = 1003,
  TOO_MANY_INFLIGHT = 1004,

  // Protocol errors (2000-2999)
  INVALID_REQUEST = 2001,
  METHOD_NOT_FOUND = 2002,
  INVALID_PARAMS = 2003,

  // Service errors (3000-3999)
  SERVICE_NOT_FOUND = 3001,
  SERVICE_ERROR = 3002,
  SERVICE_TIMEOUT = 3003,

  // Application errors (4000-4999)
  UNAUTHORIZED = 4001,
  FORBIDDEN = 4002,
  NOT_FOUND = 4003,
  CONFLICT = 4004,
  VALIDATION_ERROR = 4005,

  // Internal errors (5000-5999)
  INTERNAL_ERROR = 5000,
  NOT_IMPLEMENTED = 5001,
  NETWORK_ERROR = 1005,
  TIMEOUT_ERROR = 1006,
  SERVICE_UNAVAILABLE = 3004,
}

/**
 * Custom RPC call error
 */
export class RpcCallError extends Error {
  constructor(public readonly rpcError: RpcError) {
    super(rpcError.message);
    this.name = "RpcCallError";

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, RpcCallError);
    }
  }

  get code(): number {
    return this.rpcError.code;
  }

  get data(): unknown {
    return this.rpcError.data;
  }
}

export class DeliveryUnknownError extends Error {
  constructor(
    message = "Call delivery is unknown",
    public readonly retryable = false,
  ) {
    super(message);
    this.name = "DeliveryUnknownError";
  }
}

export class TransportNotAdmittedError extends Error {
  constructor(message = "Call was not admitted") {
    super(message);
    this.name = "TransportNotAdmittedError";
  }
}

export class OverloadedError extends Error {
  constructor(message = "V2 call admission queue is full") {
    super(message);
    this.name = "OverloadedError";
  }
}

export class CallDeadlineExceededError extends Error {
  constructor(message = "Call deadline expired before transport admission") {
    super(message);
    this.name = "CallDeadlineExceededError";
  }
}

export class CallCancelledError extends Error {
  constructor(message = "Call cancelled") {
    super(message);
    this.name = "CallCancelledError";
  }
}

export class PayloadTooLargeError extends Error {
  constructor(message = "V2 payload exceeds negotiated limits") {
    super(message);
    this.name = "PayloadTooLargeError";
  }
}

export class SessionReplacedError extends Error {
  constructor(message = "V2 control session was replaced") {
    super(message);
    this.name = "SessionReplacedError";
  }
}
